import { RADIO_SAMPLE_RATE } from "../types/radio";

export const VAD_OPEN_BLOCKS = 2;
export const VAD_HANGOVER_MS = 700;

export interface VadOptions {
  thresholdDb: number;
  sampleRate?: number;
  openBlocks?: number;
  hangoverMs?: number;
}

export interface VadStep {
  rmsDb: number;
  open: boolean;
  /** True on the step where the gate transitioned open → closed. */
  justClosed: boolean;
}

export function rmsDbfs(block: Float32Array): number {
  if (block.length === 0) return -Infinity;
  let sum = 0;
  for (let i = 0; i < block.length; i++) sum += block[i] * block[i];
  const rms = Math.sqrt(sum / block.length);
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

/**
 * Energy-gated voice activity detector.
 *
 * Opens once `openBlocks` consecutive blocks exceed the threshold and closes
 * after `hangoverMs` of audio below it. Time is derived from the number of
 * samples fed, not the wall clock, so the detector is deterministic.
 */
export class EnergyVad {
  private thresholdDb: number;
  private readonly sampleRate: number;
  private readonly openBlocks: number;
  private readonly hangoverMs: number;
  private hotStreak = 0;
  private quietMs = 0;
  private isOpen = false;

  constructor(options: VadOptions) {
    this.thresholdDb = options.thresholdDb;
    this.sampleRate = options.sampleRate ?? RADIO_SAMPLE_RATE;
    this.openBlocks = options.openBlocks ?? VAD_OPEN_BLOCKS;
    this.hangoverMs = options.hangoverMs ?? VAD_HANGOVER_MS;
  }

  get open(): boolean {
    return this.isOpen;
  }

  get threshold(): number {
    return this.thresholdDb;
  }

  setThreshold(db: number): void {
    this.thresholdDb = db;
  }

  reset(): void {
    this.hotStreak = 0;
    this.quietMs = 0;
    this.isOpen = false;
  }

  step(block: Float32Array): VadStep {
    const rmsDb = rmsDbfs(block);
    const blockMs = (block.length / this.sampleRate) * 1000;
    const hot = rmsDb > this.thresholdDb;
    let justClosed = false;

    if (hot) {
      this.hotStreak += 1;
      this.quietMs = 0;
      if (!this.isOpen && this.hotStreak >= this.openBlocks) {
        this.isOpen = true;
      }
    } else {
      this.hotStreak = 0;
      if (this.isOpen) {
        this.quietMs += blockMs;
        if (this.quietMs >= this.hangoverMs) {
          this.isOpen = false;
          this.quietMs = 0;
          justClosed = true;
        }
      }
    }

    return { rmsDb, open: this.isOpen, justClosed };
  }
}
